import Controller from '@ember/controller';
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { restartableTask, timeout } from 'ember-concurrency';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type MetricsService from 'garaje/services/metrics';
import type ReactRendererService from 'garaje/services/react-renderer';
import type StateService from 'garaje/services/state';
import zft from 'garaje/utils/zero-for-tests';
import type { Root } from 'react-dom/client';

import { PAGE_SIZE, type VisitorsProfilesRouteModel } from './route';

const DEBOUNCE_TIME = zft(300);

export default class VisitorProfilesIndexController extends Controller {
  @service declare flashMessages: FlashMessagesService;
  @service declare metrics: MetricsService;
  @service declare reactRenderer: ReactRendererService;
  @service declare router: RouterService;
  @service declare state: StateService;

  declare model: VisitorsProfilesRouteModel;

  private root?: Root;

  queryParams = ['page', 'query'];
  @tracked page = 1;
  @tracked query = ''; // this is the actual debounced filter being used
  @tracked queryLive = this.query; // this is the live value for the search input

  @tracked directoryIsLoading: boolean = false;

  @action
  setPageNumber(pageNumber: number): void {
    this.directoryIsLoading = true;
    this.page = pageNumber;
  }

  @action
  onSearchInput(value: string): void {
    value = value.slice(0, 100); // limit to 100 characters
    this.queryLive = value;
    void this.setQueryTask.perform(value);
  }

  setQueryTask = restartableTask(async (query: string) => {
    await timeout(DEBOUNCE_TIME); // debounce search
    if (this.query !== query) {
      this.directoryIsLoading = true;
      this.query = query;
      this.page = 1;
      this.metrics.trackEvent('Visitors Profiles Index Search Performed', {
        search_query: query,
      });
    } else {
      this.directoryIsLoading = false;
    }
  });

  @action
  clearSearch(): void {
    void this.setQueryTask.cancelAll();
    this.page = 1;
    this.directoryIsLoading = true;
    this.query = '';
    this.queryLive = '';
  }

  get pageCount(): number {
    return Math.ceil(this.model.visitorProfileCount / PAGE_SIZE);
  }

  get pageSize(): number {
    return PAGE_SIZE;
  }

  get pageNumber(): number {
    return this.page;
  }

  get hasProfiles(): boolean {
    return this.model.visitorProfileCount > 0;
  }

  @action
  handleNavigate(route: string, ...args: unknown[]): void {
    // @ts-expect-error - Bridging React navigation to Ember router with dynamic route args
    this.router.transitionTo(route, ...args);
  }

  @action
  showMergeSuccessFlash(): void {
    this.flashMessages.showAndHideFlash('success', 'Visitor Profiles merged');
  }

  @action
  async injectComponent(): Promise<void> {
    const { VisitorProfileView } = await import('reactRichComponents/visitors/profiles/VisitorProfileView');
    if (this.isDestroying || this.isDestroyed) return;

    this.root = await this.reactRenderer.render(VisitorProfileView, 'react__visitor-profiles-directory-view', {
      companyId: this.state.currentCompany.id,
      locationId: this.state.currentLocation.id,
      onNavigate: (route, ...args) => this.handleNavigate(route, ...args),
      onMergeSuccess: () => this.showMergeSuccessFlash(),
    });
  }

  @action
  destroyComponent(): void {
    if (this.root) {
      this.reactRenderer.unmount(this.root);
      this.root = undefined;
    }
  }
}
